import { distanceMi, type LatLon } from './geo';
import { flightLabel, type PlaceLookup, spanNights, stayName } from './items';
import type { Item, Place } from './schema';
import { effectiveTimes } from './sequence';
import { instantFromLocal } from './time';
import { legOf, type TripLeg } from './trip-model';

/**
 * A trip as a sequence to play back on a map: each flight or train draws its route, each
 * stay drops a pin, and the drives between them (airport to hotel, hotel to hotel, back to
 * the car return) follow the roads. Same idea as the World App's flight replay, for one trip.
 */

export interface Stop extends LatLon {
  name: string;
}

export type PlaybackStep =
  | {
      kind: 'move';
      itemId: string;
      leg: TripLeg;
      at: number;
      tz: string;
      title: string;
      route: string;
      miles: number;
    }
  | {
      kind: 'stay';
      itemId: string;
      place: LatLon;
      at: number;
      tz: string;
      title: string;
      nights: number;
    }
  | {
      kind: 'port';
      itemId: string;
      place: LatLon;
      at: number;
      tz: string;
      title: string;
      /** The ship's name, or the cruise line. */
      ship: string;
    }
  | {
      kind: 'drive';
      /** Unique per drive: `${from item}>${to item}`. */
      itemId: string;
      from: Stop;
      to: Stop;
      at: number;
      tz: string;
      title: string;
      /** True while a rental car is held; otherwise a taxi or ride. */
      car: boolean;
      miles: number;
    };

/** Where I am at a moment, from each plan's start and end. */
interface Presence {
  at: number;
  tz: string;
  place: Place;
  itemId: string;
  /** The far end of a flight or train: reached by that move, not by a drive. */
  arrival: boolean;
}

const hasCoords = (p: Place | undefined): p is Place & { lat: number; lon: number } =>
  p?.lat != null && p.lon != null;

/** A short name for where a drive goes: the hotel, or the airport code. */
function stopName(p: Place, items: Map<string, Item>, itemId: string): string {
  const i = items.get(itemId);
  if (i?.type === 'lodging') return stayName(i);
  return p.iata ?? p.name;
}

/**
 * Two stays at the same hotel, or two rentals from the same counter, with overlapping dates
 * are one visit booked twice (a rebooking, or a points and a cash reservation). Play them once,
 * covering both.
 */
function collapseDuplicates(items: Item[]): Item[] {
  const out: Item[] = [];
  for (const i of [...items].sort((a, b) => a.startAt! - b.startAt!)) {
    const twin =
      (i.type === 'lodging' || i.type === 'car') && i.startPlaceId
        ? out.find(
            (o) =>
              o.type === i.type &&
              o.startPlaceId === i.startPlaceId &&
              o.endAt != null &&
              o.endAt > i.startAt!,
          )
        : undefined;
    if (!twin) {
      out.push(i);
      continue;
    }
    const k = out.indexOf(twin);
    out[k] = { ...twin, endAt: Math.max(twin.endAt!, i.endAt ?? twin.endAt!) };
  }
  return out;
}

export function playbackSteps(items: Item[], places: PlaceLookup): PlaybackStep[] {
  const live = collapseDuplicates(
    items.filter((i) => !i.deleted && i.status === 'active' && i.startAt != null),
  );
  // Booked times bent around flights and trains, so the order is the order I lived it.
  const actual = effectiveTimes(live, places);
  const startOf = (i: Item) => actual.get(i.id)?.start ?? i.startAt!;
  const endOf = (i: Item) => (actual.has(i.id) ? actual.get(i.id)!.end : i.endAt);
  const byId = new Map(live.map((i) => [i.id, i]));
  const steps: PlaybackStep[] = [];
  const presence: Presence[] = [];
  const rentals: { from: number; to: number }[] = [];

  for (const i of live) {
    const start = places(i.startPlaceId);
    const end = places(i.endPlaceId) ?? start;
    const tz = i.startTz ?? start?.tz ?? 'UTC';
    const endTz = i.endTz ?? end?.tz ?? tz;
    if (i.type === 'car' && endOf(i) != null) rentals.push({ from: startOf(i), to: endOf(i)! });
    if (hasCoords(start))
      presence.push({ at: startOf(i), tz, place: start, itemId: i.id, arrival: false });
    if (hasCoords(end) && endOf(i) != null)
      presence.push({
        at: endOf(i)!,
        tz: endTz,
        place: end,
        itemId: i.id,
        arrival: legOf(i, places) != null && i.type !== 'car' && i.type !== 'transfer',
      });

    if (i.type === 'lodging') {
      if (!hasCoords(start)) continue;
      steps.push({
        kind: 'stay',
        itemId: i.id,
        place: { lat: start.lat, lon: start.lon },
        at: startOf(i),
        tz,
        title: stayName(i),
        nights: spanNights(i),
      });
      continue;
    }
    if (i.type === 'cruise' && i.details.type === 'cruise') {
      steps.push(...cruiseSteps(i, places));
      continue;
    }
    const leg = legOf(i, places);
    if (!leg || i.type === 'car' || i.type === 'transfer') continue;
    const name = (s: TripLeg['from']) => s.code ?? s.name;
    steps.push({
      kind: 'move',
      itemId: i.id,
      leg,
      at: i.startAt!,
      tz,
      title: i.type === 'flight' ? flightLabel(i) : i.title,
      route: `${name(leg.from)} to ${name(leg.to)}`,
      miles: distanceMi(leg.from, leg.to),
    });
  }

  // Walk where I am through time; every change of place that isn't a flight or train is a drive.
  presence.sort((a, b) => a.at - b.at);
  let here: Presence | null = null;
  for (const p of presence) {
    if (here && !p.arrival && distanceMi(here.place as LatLon, p.place as LatLon) > 0.3) {
      const leaving = here.at;
      const car = rentals.some((r) => leaving >= r.from && p.at <= r.to);
      const toName = stopName(p.place, byId, p.itemId);
      steps.push({
        kind: 'drive',
        itemId: `${here.itemId}>${p.itemId}`,
        from: {
          lat: here.place.lat!,
          lon: here.place.lon!,
          name: stopName(here.place, byId, here.itemId),
        },
        to: { lat: p.place.lat!, lon: p.place.lon!, name: toName },
        // Just before arriving, so it plays before the stay or flight it leads to.
        at: p.at - 1,
        tz: p.tz,
        title: `${car ? 'Drive' : 'Ride'} to ${toName}`,
        car,
        miles: distanceMi(here.place as LatLon, p.place as LatLon),
      });
    }
    here = p;
  }

  return steps.sort((a, b) => a.at - b.at);
}

/**
 * A cruise as sailing legs between embarking, each port of call and disembarking, with a
 * pin for each port. Ports without a date are spaced evenly through the cruise.
 */
function cruiseSteps(i: Item, places: PlaceLookup): PlaybackStep[] {
  if (i.details.type !== 'cruise' || i.startAt == null) return [];
  const start = places(i.startPlaceId);
  const end = places(i.endPlaceId) ?? start;
  if (!hasCoords(start) || !hasCoords(end)) return [];
  const tz = i.startTz ?? start.tz ?? 'UTC';
  const until = i.endAt ?? i.startAt;
  const ports = i.details.ports ?? [];
  const ship = i.details.ship ?? i.details.line ?? i.title;
  const stopsAt = ports.map((p, k) =>
    p.date
      ? instantFromLocal(`${p.date}T12:00`, tz)
      : i.startAt! + ((k + 1) * (until - i.startAt!)) / (ports.length + 1),
  );
  const points = [
    { name: start.city ?? start.name, lat: start.lat, lon: start.lon, at: i.startAt },
    ...ports.map((p, k) => ({ name: p.name, lat: p.lat, lon: p.lon, at: stopsAt[k]! })),
    { name: end.city ?? end.name, lat: end.lat, lon: end.lon, at: until },
  ];
  const out: PlaybackStep[] = [];
  for (let k = 1; k < points.length; k++) {
    const a = points[k - 1]!;
    const b = points[k]!;
    out.push({
      kind: 'move',
      itemId: `${i.id}#${k}`,
      leg: {
        itemId: `${i.id}#${k}`,
        kind: 'sea',
        from: { lat: a.lat, lon: a.lon, code: null, name: a.name },
        to: { lat: b.lat, lon: b.lon, code: null, name: b.name },
      },
      at: a.at,
      tz,
      title: ship,
      route: `${a.name} to ${b.name}`,
      miles: distanceMi(a, b),
    });
    if (k < points.length - 1)
      out.push({
        kind: 'port',
        itemId: `${i.id}@${k}`,
        place: { lat: b.lat, lon: b.lon },
        at: b.at,
        tz,
        title: b.name,
        ship,
      });
  }
  return out;
}

/** How long a step plays at 1x: long hauls take a little longer, stays and drives a beat. */
export function stepDuration(step: PlaybackStep): number {
  if (step.kind === 'move') return 900 + Math.min(1800, step.miles * 0.4);
  if (step.kind === 'drive') return 1400 + Math.min(1600, step.miles * 60);
  if (step.kind === 'port') return 1000;
  return 1100;
}
